import React, { Suspense } from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { AppContextProvider } from "./context/AppContext";
import { AuthProvider } from "./context/AuthContext";

import "react-toastify/dist/ReactToastify.css";
import "./assets/styles/custom.css";
import "./assets/styles/main.css";
import "./assets/styles/responsive.css";

import { ToastContainer } from "react-toastify";
import Loader from "./components/Loader";
import ScrollToTop from "./components/ScrollToTop";
import SignIn from "./pages/Auth/SignIn";
import Dashboard from "./pages/Dashboard";
import HotelModule from "./pages/Hotel/HotelModule";
import HotelList from "./pages/Hotel/HotelList";
import AirlineModule from "./pages/Airline/AirlineModule";
import AirlineDashboard from "./pages/Airline/AirlineDashboard";
import RegistrationCard from "./pages/Airline/RegistrationCard";
import AgentBooking from "./pages/Agent/AgentBooking";
import AgentList from "./pages/Agent/AgentList";
import AddAgent from "./pages/Agent/AddAgent";
import Hotels from "./pages/Agent/Hotels";
import BookingEnquiries from "./pages/Agent/BookingEnquiries";
import PickedBookingsManager from "./pages/Agent/PickedBookingsManager";
import AgentDashboard from "./pages/Agent/AgentDashboard";
import AgentChangePassword from "./pages/Agent/ChangePassword";
import Bookings from "./pages/Bookings/Bookings";
import PrivateRoute from "./components/PrivateRoute";
import ChangePassword from "./pages/Auth/ChangePassword";
import SendNotification from "./pages/Notifications/SendNotification";
import NotificationHistory from "./pages/Notifications/NotificationHistory";
import GuestList from "./pages/Guest/GuestList";
import BusDashboard from "./pages/Bus/BusDashboard";
import DailyTripReports from "./pages/Bus/DailyTripReports";
import BusCompanyList from "./pages/Bus/BusCompanyList";

window.Buffer = window.Buffer || require("buffer").Buffer;

const getCurrentRole = () => {
  const explicitRole = localStorage.getItem("authRole");
  if (explicitRole) return explicitRole;
  try {
    const profile = JSON.parse(localStorage.getItem("userProfile") || "{}");
    const userType = profile?.userType || profile?.user_type;
    if (userType === "Super_Admin") return "admin";
    if (userType === "HotelPartner") return "hotel";
    if (userType === "Agent") return "agent";
    if (userType === "Airline") return "airline";
    if (userType === "bus_company") return "bus";
  } catch (_err) {
    return "admin";
  }
  return "admin";
};

const RoleHomeRedirect = () => {
  const role = getCurrentRole();
  if (role === "hotel") return <Navigate to="/dashboard" replace />;
  if (role === "agent") return <Navigate to="/agent-dashboard" replace />;
  if (role === "airline") return <Navigate to="/airline-dashboard" replace />;
  if (role === "bus") return <Navigate to="/bus-dashboard" replace />;
  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <AuthProvider>
      <AppContextProvider>
        <Suspense fallback={<Loader />}>
          <BrowserRouter>
            <ScrollToTop />
            <ToastContainer closeOnClick={false} />
            <AppRoutes />
          </BrowserRouter>
        </Suspense>
      </AppContextProvider>
    </AuthProvider>
  );
}

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/admin/login" element={<SignIn />} />
      <Route path="/login" element={<SignIn />} />
      <Route
        path="/hotel-management"
        element={
          <PrivateRoute allowedRoles={["admin", "hotel", "airline"]}>
            <HotelList />
          </PrivateRoute>
        }
      />
      <Route
        path="/hotel-management/new"
        element={
          <PrivateRoute allowedRoles={["admin", "hotel", "airline"]}>
            <HotelModule />
          </PrivateRoute>
        }
      />
      {/* Default: if logged in go to hotel module, otherwise login */}
      <Route
        path="/"
        element={
          <PrivateRoute>
            <RoleHomeRedirect />
          </PrivateRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute allowedRoles={["admin", "hotel"]}>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/airline-dashboard"
        element={
          <PrivateRoute allowedRoles={["admin", "airline"]}>
            <AirlineDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/airline-management"
        element={
          <PrivateRoute allowedRoles={["admin", "airline"]}>
            <AirlineModule />
          </PrivateRoute>
        }
      />
      <Route
        path="/airline/registration-card"
        element={
          <PrivateRoute allowedRoles={["admin", "airline"]}>
            <RegistrationCard />
          </PrivateRoute>
        }
      />
      <Route
        path="/agent-dashboard"
        element={
          <PrivateRoute allowedRoles={["admin", "agent"]}>
            <AgentDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/agent-booking"
        element={
          <PrivateRoute allowedRoles={["admin", "agent"]}>
            <AgentBooking />
          </PrivateRoute>
        }
      />
      <Route
        path="/agent-management"
        element={
          <PrivateRoute allowedRoles={["admin", "hotel"]}>
            <AgentList />
          </PrivateRoute>
        }
      />
      <Route
        path="/agent-management/add"
        element={
          <PrivateRoute allowedRoles={["hotel"]}>
            <AddAgent />
          </PrivateRoute>
        }
      />
      <Route
        path="/agent-hotels"
        element={
          <PrivateRoute allowedRoles={["admin", "agent"]}>
            <Hotels />
          </PrivateRoute>
        }
      />
      <Route
        path="/agent-booking-enquires"
        element={
          <PrivateRoute allowedRoles={["admin", "agent", "hotel"]}>
            <BookingEnquiries />
          </PrivateRoute>
        }
      />
      <Route
        path="/agent-picked-bookings"
        element={
          <PrivateRoute allowedRoles={["admin", "agent"]}>
            <PickedBookingsManager />
          </PrivateRoute>
        }
      />
      <Route
        path="/agent-change-password"
        element={
          <PrivateRoute allowedRoles={["admin", "agent"]}>
            <AgentChangePassword />
          </PrivateRoute>
        }
      />
      <Route
        path="/hotel-management/:id"
        element={
          <PrivateRoute allowedRoles={["admin", "hotel", "airline"]}>
            <HotelModule />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/guest"
        element={
          <PrivateRoute allowedRoles={["admin", "airline"]}>
            <GuestList />
          </PrivateRoute>
        }
      />
      <Route
        path="/bookings"
        element={
          <PrivateRoute allowedRoles={["admin", "hotel", "airline"]}>
            <Bookings />
          </PrivateRoute>
        }
      />
      <Route
        path="/change-password"
        element={
          <PrivateRoute allowedRoles={["admin", "hotel"]}>
            <ChangePassword />
          </PrivateRoute>
        }
      />
      <Route
        path="/notifications/send"
        element={
          <PrivateRoute allowedRoles={["admin"]}>
            <SendNotification />
          </PrivateRoute>
        }
      />
      <Route
        path="/notifications/history"
        element={
          <PrivateRoute allowedRoles={["admin"]}>
            <NotificationHistory />
          </PrivateRoute>
        }
      />
      <Route
        path="/bus-dashboard"
        element={
          <PrivateRoute allowedRoles={["admin", "bus"]}>
            <BusDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/bus-reports"
        element={
          <PrivateRoute allowedRoles={["admin", "bus"]}>
            <DailyTripReports />
          </PrivateRoute>
        }
      />
      <Route
        path="/bus-companies"
        element={
          <PrivateRoute allowedRoles={["admin"]}>
            <BusCompanyList />
          </PrivateRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default App;
